import React, { useState, useCallback } from 'react'
import PlacesAutocomplete from 'react-places-autocomplete'
import scriptLoader from 'react-async-script-loader'
import styled, { css } from 'styled-components'
import useOutsideClick from '../../util/click'
import { H5, SM, Input } from '@foodsby/nutrient'
import { MAPS_SCRIPT_URL } from '../location-search/LocationSearchBox'
import { connect } from 'react-redux'
import { getDayName } from '../../util/dateUtils'
import { search } from '../../redux/modules/locationSearch'
import { space, flexbox } from 'styled-system'
import pluralize from 'pluralize'

const getHighlightedText = (text, highlight) => {
  const highlightParts = highlight.split(' ')
  const parts = text.split(' ')

  return (
    <span>
      {parts &&
        parts.map((part, i) => (
          <span
            key={i}
            style={
              highlightParts &&
              highlightParts.some(
                highlight =>
                  highlight &&
                  part
                    .replace(/[^a-z0-9]/gi, '')
                    .toLowerCase()
                    .indexOf(
                      highlight.toLowerCase().replace(/[^a-z0-9]/gi, ''),
                    ) > -1,
              )
                ? { fontWeight: 'bold' }
                : {}
            }
          >
            {`${part} `}
          </span>
        ))}
    </span>
  )
}

const Wrapper = styled.div`
  position: relative;
  ${space}
`

const StyledInput = styled(Input)`
  background: ${props => props.theme.color.white};
  border-bottom: solid 1px ${props => props.theme.color.greyShade1};
  border-radius: calc(${props => props.theme.baseUnit} * 5);
  border: solid 1px ${props => props.theme.color.greyShade1};
  color: ${props => props.theme.group1shade9};
  font-family: ${props => props.theme.fontFamily};
  font-size: calc(${props => props.theme.baseUnit} * 4);
  font-weight: bold;
  height: calc(${props => props.theme.baseUnit} * 8);

  ${props =>
    props.dropdownVisible &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}

  &::placeholder {
    font-weight: normal;
  }

  &:focus {
    background: ${props => props.theme.color.white};
    border: solid 1px ${props => props.theme.color.greyShade1};
    box-shadow: none;
  }
`

const DropDown = styled.div`
  background-color: ${props => props.theme.color.white};
  border-bottom-left-radius: calc(${props => props.theme.baseUnit} * 4);
  border-bottom-right-radius: calc(${props => props.theme.baseUnit} * 4);
  border: none;
  color: ${props => props.theme.group1shade9};
  font-family: ${props => props.theme.fontFamily};
  font-size: calc(${props => props.theme.baseUnit} * 4);
  position: absolute;
  top: calc(${props => props.theme.baseUnit} * 8 + 1);
  width: 100%;
  z-index: 1;
  overflow: hidden;

  ${props =>
    props.dropdownVisible &&
    css`
      border: solid 1px ${props => props.theme.color.greyShade1};
      border-top: none;
    `}

  & .-suggestion {
    padding: ${props => props.theme.baseUnit}
      calc(${props => props.theme.baseUnit} * 3);

    & .-active,
    &:hover {
      background-color: ${props => props.theme.color.brandPrimary};
      color: ${props => props.theme.color.white};
    }
  }
`

const SearchModal = ({ initialAddress = '', search, ...rest }) => {
  const [address, changeAddress] = useState(initialAddress)
  return (
    <PlacesAutocomplete
      googleCallbackName="loadGoogleMaps"
      highlightFirstSuggestion
      onChange={changeAddress}
      onError={() => {}}
      onSelect={search}
      value={address}
    >
      {({ getInputProps, getSuggestionItemProps, suggestions }) => {
        const dropdownVisible = suggestions.length > 0
        return (
          <Wrapper {...rest}>
            <StyledInput
              {...getInputProps({
                placeholder: '123 Spaghetti Street',
                autoFocus: true,
                id: 'searchbox',
                dropdownVisible,
                onKeyDown: e => {
                  if (!suggestions.length && e.keyCode === 13) {
                    search(this.state.address)
                  }
                },
              })}
            />
            <DropDown dropdownVisible={dropdownVisible}>
              {suggestions.map((suggestion, i) => {
                const className = suggestion.active
                  ? '-suggestion -active'
                  : '-suggestion'

                return (
                  <div
                    key={i}
                    {...getSuggestionItemProps(suggestion, {
                      className,
                    })}
                  >
                    <span>
                      {getHighlightedText(
                        suggestion.description,
                        getInputProps().value,
                      )}
                    </span>
                  </div>
                )
              })}
              {suggestions && suggestions.length > 0 && (
                <div className="autocomplete-suggestion google-attribute">
                  <span>
                    <img
                      alt="Powered by Google"
                      src="https://images.foodsby.com/third-party/powered_by_google_on_white.png"
                    />
                  </span>
                </div>
              )}
            </DropDown>
          </Wrapper>
        )
      }}
    </PlacesAutocomplete>
  )
}

const matchStateToProps = state => {
  const { loading, error } = state.locationSearch

  return {
    loading,
    error,
  }
}

const matchDispatchToProps = { search }

const SearchModalWithScripts = scriptLoader(MAPS_SCRIPT_URL)(
  connect(matchStateToProps, matchDispatchToProps)(SearchModal),
)

export const YourLocation = ({ name, selectedDate, orderCount, ...rest }) => {
  // State used for displaying location input
  const [isEditingLocation, setIsEditingLocation] = useState(false)
  const [curRef, setCurRef] = useState({ current: undefined })
  const ref = useCallback(node => {
    setCurRef({ current: node })
  }, [])
  const dayText =
    orderCount > 0
      ? `${getDayName(selectedDate)}'s ${pluralize('order', orderCount)}`
      : getDayName(selectedDate)

  useOutsideClick(curRef, () => {
    setIsEditingLocation(false)
  })

  return (
    <Flex className="locationContainer" {...rest}>
      <H5>{dayText}</H5>
      <Component>
        {!isEditingLocation ? (
          <Address>
            <i className="icon-location secondary" />
            <SM
              className="-locationName"
              onClick={() => {
                setIsEditingLocation(true)
              }}
            >
              {name}
            </SM>
          </Address>
        ) : (
          <div ref={ref}>
            <SearchModalWithScripts />
          </div>
        )}
      </Component>
    </Flex>
  )
}

YourLocation.defaultProps = {
  address: {},
}
const Flex = styled.div`
  ${flexbox};
  ${space};

  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 4px;

  // TODO: pull this media query in as a var from Nutrient
  @media screen and (min-width: 320px) and (max-width: 640px) {
    flex-direction: column-reverse;
  }
`

const Component = styled.div`
  display: flex;
  flex-direction: column;
  width: 75%;
  z-index: 2;

  @media screen and (min-width: 320px) and (max-width: 640px) {
    margin-bottom: 20px;
    width: 100%;
  }
`

const Address = styled.div`
  align-self: flex-end;
  color: #59969b;
  display: flex;

  & .-locationName {
    color: #59969b;
    font-size: 14px;
    text-align: center;
    text-decoration: underline;

    &:hover {
      cursor: pointer;
    }
  }

  @media screen and (min-width: 320px) and (max-width: 640px) {
    align-self: center;
  }
`

export default Component
